import React, { useState, useRef, useEffect } from "react";
import { Modal, Icon, Row, Col, Spin } from "antd";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import { convertBlobToBase64 } from "../../../services";
import { photoEditAction } from "../../../models";
import { getIconUrl } from "../../../services/asset";
import { getDocumentIcon } from "../../../utils/documentIcons";
import {
  CHAT_EDITED_IMAGE_FILENAME,
  CHAT_EDITED_IMAGE_TYPE,
  CHAT_CROPPER_INIT_TIMEOUT_MS,
  CHAT_CROPPER_MAX_WAIT_ATTEMPTS,
  CHAT_CROPPER_WAIT_INTERVAL_MS
} from "./constants";
import styles from "./chatFileUploader.scss";

interface FileWithPreview {
  file: File;
  id: string;
  type: 'image' | 'document';
  preview?: string;
  status?: 'uploading' | 'done' | 'error';
}

interface ChatFileUploaderProps {
  onFilesSelected: (files: FileWithPreview[]) => void;
  disabled?: boolean;
  selectedFiles?: FileWithPreview[];
}



export const ChatFileUploader = ({ onFilesSelected, selectedFiles: externalSelectedFiles, disabled }: ChatFileUploaderProps) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [editPhotoData, setEditPhotoData] = useState('');
  const [cropper, setCropper] = useState(null);
  const [editingFileId, setEditingFileId] = useState(null);

  // Sync with external selectedFiles prop (for clearing)
  useEffect(() => {
    if (externalSelectedFiles !== undefined) {
      setSelectedFiles(externalSelectedFiles);
    }
  }, [externalSelectedFiles]);


  const handleRemoveFile = (id: string) => {
    const updated = selectedFiles.filter(f => f.id !== id);
    setSelectedFiles(updated);
    onFilesSelected(updated);
  };

  const handlePreview = async (file) => {
    if (file.type === 'image' && file.preview) {
      setPreviewVisible(true);
      setEditingFileId(file.id);

      // Load image data for editing
      try {
        const blob = await fetch(file.preview).then(response => response.blob());
        const base64 = await convertBlobToBase64(blob);
        setEditPhotoData(base64);
      } catch (error) {
        console.error('Failed to load image for editing:', error);
      }
    }
  };

  const handleRenderedEditor = (ref) => {
    if (ref && editPhotoData) {
      ref.onload = () => {
        // Add a small delay to ensure the image is fully rendered
        setTimeout(() => {
          const newCropper = new Cropper(ref, {
            zoomable: false,
            scalable: true,
            rotatable: true,
            autoCrop: false,
            checkOrientation: false,
            ready() {
              // Cropper is now ready for operations
            }
          });
          setCropper(newCropper);
        }, CHAT_CROPPER_INIT_TIMEOUT_MS);
      };
      ref.src = editPhotoData;
    }
  };

  const handleEditAction = (action) => {
    if (!cropper) {
      console.warn('Cropper not initialized');
      return;
    }

    // Wait for cropper to be ready with a timeout
    const waitForCropper = (callback, attempts = 0) => {
      if (attempts > CHAT_CROPPER_MAX_WAIT_ATTEMPTS) {
        console.error('Cropper failed to initialize within timeout');
        return;
      }

      try {
        // Test if cropper is functional
        cropper.getCanvasData();
        callback();
      } catch (e) {
        // Cropper not ready yet, wait a bit more
        setTimeout(() => waitForCropper(callback, attempts + 1), CHAT_CROPPER_WAIT_INTERVAL_MS);
      }
    };

    waitForCropper(() => {
      // Double-check cropper still exists before operations
      if (!cropper) {
        console.error('Cropper was destroyed during wait');
        return;
      }

      try {
        switch (action) {
          case photoEditAction.rotateLeft:
            if (cropper && typeof cropper.rotate === 'function') {
              cropper.rotate(-90);
            }
            break;
          case photoEditAction.rotateRight:
            if (cropper && typeof cropper.rotate === 'function') {
              cropper.rotate(90);
            }
            break;
          case photoEditAction.flipHorizontal:
            if (cropper && typeof cropper.scaleX === 'function') {
              try {
                const currentData = cropper.getData();
                const currentScaleX = currentData && currentData.scaleX !== undefined ? currentData.scaleX : 1;
                cropper.scaleX(-currentScaleX);
              } catch (e) {
                // Fallback for scaling issues
                cropper.scaleX(-1);
              }
            }
            break;
          case photoEditAction.flipVertical:
            if (cropper && typeof cropper.scaleY === 'function') {
              try {
                const currentData = cropper.getData();
                const currentScaleY = currentData && currentData.scaleY !== undefined ? currentData.scaleY : 1;
                cropper.scaleY(-currentScaleY);
              } catch (e) {
                // Fallback for scaling issues
                cropper.scaleY(-1);
              }
            }
            break;
          case photoEditAction.reset:
            if (cropper && typeof cropper.reset === 'function') {
              cropper.reset();
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Error in handleEditAction:', error);
      }
    });
  };

  const applyEditPhoto = () => {
    if (cropper) {
      const canvas = cropper.getCroppedCanvas();
      if (canvas) {
        canvas.toBlob((blob) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const editedImageUrl = e.target.result;
            // Create a new File object from the edited blob
            const editedFile = new File([blob], CHAT_EDITED_IMAGE_FILENAME, { type: CHAT_EDITED_IMAGE_TYPE });

            // Update both the preview and the actual file for the specific file being edited
            setSelectedFiles(prev => {
              const updated = prev.map(file => {
                if (file.id === editingFileId) {
                  return {
                    ...file,
                    preview: editedImageUrl,
                    file: editedFile
                  };
                }
                return file;
              });
              onFilesSelected(updated);
              return updated;
            });
          };
          reader.readAsDataURL(blob);
        });
      }
    }
    handleCancel();
  };

  const handleCancel = () => {
    setPreviewVisible(false);
    setEditPhotoData('');
    setEditingFileId(null);
    if (cropper) {
      cropper.destroy();
      setCropper(null);
    }
  };


  return (
    <div className={styles.chatFileUploader}>

      {selectedFiles.length > 0 && (
        <div className={styles.filesPreview}>
          <Row gutter={[12, 12]}>
            {selectedFiles.map((file, index) => (
              <Col key={file.id} className={styles.fileColWrapper} xs={12} sm={8} md={6} lg={4}>
                <div className={styles.fileCard}>
                  {file.type === 'image' ? (
                    <div
                      className={styles.imageCard}
                      onClick={() => !disabled && handlePreview(file)}
                      style={disabled ? { cursor: 'not-allowed' } : {}}
                    >
                      {file.status === 'uploading' ? (
                        <div className={styles.loadingCard}>
                          <Spin indicator={<Icon type="loading-3-quarters" spin />} />
                        </div>
                      ) : file.preview ? (
                        <React.Fragment>
                          <img src={file.preview} alt={file.file.name} />
                          <div className={styles.hoverMenu}>
                            <img
                              role="presentation"
                              alt="delete"
                              src={getIconUrl("delete-icon.svg")}
                              className={styles.deleteButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!disabled) handleRemoveFile(file.id);
                              }}
                              style={disabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                            />
                          </div>
                        </React.Fragment>
                      ) : (
                        <div className={styles.documentCard}>
                          <div className={styles.documentIcon}>
                            {getDocumentIcon({ fileName: file.file.name, size: 24 })}
                          </div>
                          <div className={styles.documentName}>{file.file.name}</div>
                          <img
                            role="presentation"
                            alt="delete"
                            src={getIconUrl("delete-icon.svg")}
                            className={styles.deleteButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!disabled) handleRemoveFile(file.id);
                            }}
                            style={disabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    file.status === 'uploading' ? (
                      <div className={styles.loadingCard}>
                        <Spin indicator={<Icon type="loading-3-quarters" spin />} />
                      </div>
                    ) : (
                      <div className={styles.documentCard}>
                        <div className={styles.documentIcon}>
                          {getDocumentIcon({ fileName: file.file.name, size: 24 })}
                        </div>
                        <div className={styles.documentName}>{file.file.name}</div>
                        <img
                          role="presentation"
                          alt="delete"
                          src={getIconUrl("delete-icon.svg")}
                          className={styles.deleteButton}
                          onClick={() => !disabled && handleRemoveFile(file.id)}
                          style={disabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                        />
                      </div>
                    )
                  )}
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <Modal
        width="90%"
        className={styles.modal}
        visible={previewVisible}
        onCancel={handleCancel}
        okText="Spremi"
        onOk={applyEditPhoto}
        destroyOnClose
      >
        <div className={styles.content}>
          <img
            className={styles.photoEditor}
            alt="Uploaded"
            ref={handleRenderedEditor}
          />
          <div className={styles.controls}>
            <Icon
              className={styles.rotateLeft}
              type="reload"
              title="Zakreni lijevo"
              onClick={() => handleEditAction(photoEditAction.rotateLeft)}
            />
            <Icon
              className={styles.rotateRight}
              type="reload"
              title="Zakreni desno"
              onClick={() => handleEditAction(photoEditAction.rotateRight)}
            />
            <Icon
              className={styles.flipHorizontal}
              type="swap"
              title="Okreni vodoravno"
              onClick={() => handleEditAction(photoEditAction.flipHorizontal)}
            />
            <Icon
              className={styles.flipVertical}
              type="swap"
              title="Okreni okomito"
              onClick={() => handleEditAction(photoEditAction.flipVertical)}
            />
            <Icon
              className={styles.reset}
              type="sync"
              title="Resetiraj"
              onClick={() => handleEditAction(photoEditAction.reset)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};


// WEBPACK FOOTER //
// ./src/containers/ChatsPage/SingleChat/ChatFileUploader.tsx